import { BsCalendar, BsTelephone } from 'react-icons/bs';

import { getInitials, truncateName } from '#lib/textUtils';

import { formatEuro, formatDate, formatLossReason } from '../../../utils/formatters';

/**
 * Column config for the pipeline list view.
 *
 * @param {Object} params
 * @param {Object<string, Object>} params.stageMap - Stage by id
 * @param {boolean} params.aircallConnected
 * @param {boolean} params.dialerActive
 * @param {(dealId: string) => void} params.onStartDialerFromDeal
 * @returns {Array<Object>}
 */
export const buildDealListColumns = ({ stageMap, aircallConnected, dialerActive, onStartDialerFromDeal }) => [
	{
		key: 'title',
		label: 'Deal',
		minWidth: 160,
		sortable: true,
		render: (deal) => (
			<span className="block font-semibold text-text truncate" title={deal.title}>
				{deal.title}
			</span>
		),
	},
	{
		key: 'companyNameSnapshot',
		label: 'Unternehmen',
		minWidth: 150,
		sortable: true,
		cellClassName: 'truncate max-w-[180px]',
		render: (deal) => <span title={deal.companyNameSnapshot}>{truncateName(deal.companyNameSnapshot)}</span>,
	},
	{
		key: 'stage',
		label: 'Stage',
		minWidth: 130,
		render: (deal) => {
			// Only surfaced on lost deals — the reason is cleared when a deal reopens.
			const lossReasonLabel = deal.status === 'lost' ? formatLossReason(deal) : null;

			return (
				<>
					<span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-primary-100 text-primary-500">
						<span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-primary-500" />
						{stageMap[deal.stageId]?.name ?? '–'}
					</span>
					{/* The reason (and its note) sit in the tooltip — the row has no spare column. */}
					{lossReasonLabel && (
						<span
							className="ml-1.5 inline-flex max-w-[140px] items-center truncate rounded-full bg-warning-200 px-2 py-0.5 text-xs font-semibold text-warning-700"
							title={lossReasonLabel}
						>
							{deal.lossReason || deal.lossReasonNote}
						</span>
					)}
				</>
			);
		},
	},
	{
		key: 'dealValue',
		label: 'Wert',
		minWidth: 100,
		sortable: true,
		align: 'right',
		cellClassName: 'font-semibold tabular-nums whitespace-nowrap',
		render: (deal) => formatEuro(deal.dealValue),
	},
	{
		key: 'closingChance',
		label: 'Wahrsch.',
		minWidth: 90,
		sortable: true,
		align: 'right',
		cellClassName: 'text-bgBase-800 tabular-nums',
		render: (deal) => {
			const prob = deal.closingChance !== null ? Math.round(Number(deal.closingChance) * 100) : null;
			return prob !== null ? `${prob}%` : '–';
		},
	},
	{
		key: 'expectedCloseAt',
		label: 'Erw. Abschluss',
		minWidth: 120,
		sortable: true,
		cellClassName: 'text-bgBase-800 whitespace-nowrap',
		render: (deal) => (
			<span className="inline-flex items-center gap-1">
				<BsCalendar className="w-2.5 h-2.5" /> {formatDate(deal.expectedCloseAt)}
			</span>
		),
	},
	{
		key: 'createdAt',
		label: 'Erstellt am',
		minWidth: 110,
		sortable: true,
		cellClassName: 'text-bgBase-800 whitespace-nowrap',
		render: (deal) => (
			<span className="inline-flex items-center gap-1">
				<BsCalendar className="w-2.5 h-2.5" /> {formatDate(deal.createdAt)}
			</span>
		),
	},
	{
		key: 'owner',
		label: 'Owner',
		minWidth: 110,
		render: (deal) =>
			deal.users?.length > 0 ? (
				<div className="flex -space-x-1">
					{deal.users.slice(0, 3).map((u) => (
						<div
							key={u.id}
							className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold border border-white"
							title={u.fullName ?? u.emailAddress}
						>
							{getInitials(u.fullName ?? u.emailAddress)}
						</div>
					))}
				</div>
			) : (
				<span className="text-bgBase-800">–</span>
			),
	},
	{
		key: 'actions',
		label: '',
		render: (deal) => {
			const hasPhone = Boolean(deal.primaryContactPhone || deal.companyPhoneNumber);
			if (!aircallConnected || dialerActive || !hasPhone) return null;

			return (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onStartDialerFromDeal(deal.id);
					}}
					className="opacity-0 group-hover/row:opacity-100 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-100 px-1.5 py-0.5 rounded-control transition-[color,background-color,opacity]"
					title="Power Dialer ab hier starten"
				>
					<BsTelephone className="w-2.5 h-2.5" /> Ab hier
				</button>
			);
		},
	},
];
